import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Button, Upload, message, Tag, Select, Space, Result, Row, Col, Statistic, Popconfirm } from 'antd';
import { UploadOutlined, InboxOutlined, MailOutlined, SendOutlined, ReloadOutlined } from '@ant-design/icons';
import { loiAPI } from '../services/api';

const { Dragger } = Upload;
const { Option } = Select;

function LOIManagePage() {
  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Tab 1: Product Management
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productImporting, setProductImporting] = useState(false);
  const [productImportResult, setProductImportResult] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Tab 2: Dealer Management
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [dealerImporting, setDealerImporting] = useState(false);
  const [dealerImportResult, setDealerImportResult] = useState(null);
  const [dealerFile, setDealerFile] = useState(null);
  const [selectedDealerKeys, setSelectedDealerKeys] = useState([]);

  // Tab 3: Email Management
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);

  // Load campaigns on mount
  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const res = await loiAPI.getCampaigns();
      setCampaigns(res.data || []);
    } catch (error) {
      message.error('載入活動列表失敗');
    }
  };

  const handleCampaignChange = (campaignId) => {
    setSelectedCampaign(campaignId);
    setProductImportResult(null);
    setDealerImportResult(null);
    setSelectedBrand(null);
    setSelectedDealerKeys([]);
  };

  // ============ Tab 1: Product Management ============

  useEffect(() => {
    if (selectedCampaign) {
      loadProducts();
    }
  }, [selectedCampaign, selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProducts = async () => {
    if (!selectedCampaign) return;
    setProductsLoading(true);
    try {
      const res = await loiAPI.getProducts(selectedCampaign, selectedBrand);
      const items = res.data || [];
      setProducts(items);
      // Extract unique brands
      const brandMap = {};
      items.forEach((p) => {
        if (p.brand_id && p.brand_name) {
          brandMap[p.brand_id] = p.brand_name;
        }
      });
      if (!selectedBrand) {
        setBrands(Object.entries(brandMap).map(([id, name]) => ({ id, name })));
      }
    } catch (error) {
      message.error('載入產品失敗');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleProductImport = async () => {
    if (!selectedCampaign) {
      message.warning('請先選擇活動');
      return;
    }
    if (!productFile) {
      message.warning('請先選擇檔案');
      return;
    }
    setProductImporting(true);
    setProductImportResult(null);
    try {
      const res = await loiAPI.importProducts(selectedCampaign, productFile);
      setProductImportResult(res.data);
      setProductFile(null);
      message.success('產品匯入成功');
      loadProducts();
    } catch (error) {
      message.error(`產品匯入失敗: ${error.message}`);
    } finally {
      setProductImporting(false);
    }
  };

  const productColumns = [
    { title: '項次', dataIndex: 'seq', key: 'seq', width: 70, render: (_, __, idx) => idx + 1 },
    { title: '品牌', dataIndex: 'brand_name', key: 'brand_name', width: 120 },
    { title: '品項名稱', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: '授權數', dataIndex: 'license_count', key: 'license_count', width: 80 },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: '級距', dataIndex: 'tier', key: 'tier', width: 100 },
    { title: '報價', dataIndex: 'price', key: 'price', width: 120, render: (val) => val != null ? `$${Number(val).toLocaleString()}` : '-' },
  ];

  const CampaignSelector = ({ style }) => (
    <Select
      placeholder="選擇活動"
      value={selectedCampaign}
      onChange={handleCampaignChange}
      style={{ width: 300, ...style }}
      allowClear
    >
      {campaigns.map((c) => (
        <Option key={c.id} value={c.id}>{c.name}</Option>
      ))}
    </Select>
  );

  const renderProductTab = () => (
    <div>
      <Card title="匯入產品" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <CampaignSelector />
          <Dragger
            accept=".xlsx,.xls"
            maxCount={1}
            fileList={productFile ? [productFile] : []}
            beforeUpload={(file) => {
              setProductFile(file);
              return false;
            }}
            onRemove={() => setProductFile(null)}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">點擊或拖曳 Excel 檔案至此處</p>
            <p className="ant-upload-hint">支援 .xlsx / .xls 格式</p>
          </Dragger>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleProductImport}
            loading={productImporting}
            disabled={!selectedCampaign || !productFile}
          >
            匯入產品
          </Button>
        </Space>
      </Card>

      {productImportResult && (
        <Card style={{ marginBottom: 16 }}>
          <Result
            status="success"
            title="匯入完成"
            subTitle={`共匯入 ${productImportResult.total || 0} 筆產品，新建 ${productImportResult.created || 0} 筆`}
          >
            {productImportResult.brands && productImportResult.brands.length > 0 && (
              <Row gutter={16}>
                {productImportResult.brands.map((b, idx) => (
                  <Col key={idx} span={8}>
                    <Statistic title={b.name} value={b.count || 0} suffix={`筆 (${b.ranges || 0} 級距)`} />
                  </Col>
                ))}
              </Row>
            )}
          </Result>
        </Card>
      )}

      <Card title="產品列表">
        {brands.length > 0 && (
          <Tabs
            activeKey={selectedBrand || 'all'}
            onChange={(key) => setSelectedBrand(key === 'all' ? null : key)}
            style={{ marginBottom: 16 }}
            items={[
              { key: 'all', label: '全部' },
              ...brands.map((b) => ({ key: b.id, label: b.name })),
            ]}
          />
        )}
        <Table
          columns={productColumns}
          dataSource={products}
          rowKey="id"
          loading={productsLoading}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );

  // ============ Tab 2: Dealer Management ============

  useEffect(() => {
    if (selectedCampaign) {
      loadDealers();
    }
  }, [selectedCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDealers = async () => {
    if (!selectedCampaign) return;
    setDealersLoading(true);
    try {
      const res = await loiAPI.getDealers(selectedCampaign);
      setDealers(res.data || []);
    } catch (error) {
      message.error('載入經銷商失敗');
    } finally {
      setDealersLoading(false);
    }
  };

  const handleDealerImport = async () => {
    if (!selectedCampaign) {
      message.warning('請先選擇活動');
      return;
    }
    if (!dealerFile) {
      message.warning('請先選擇檔案');
      return;
    }
    setDealerImporting(true);
    setDealerImportResult(null);
    try {
      const res = await loiAPI.importDealers(selectedCampaign, dealerFile);
      setDealerImportResult(res.data);
      setDealerFile(null);
      message.success('經銷商匯入成功');
      loadDealers();
    } catch (error) {
      message.error(`經銷商匯入失敗: ${error.message}`);
    } finally {
      setDealerImporting(false);
    }
  };

  const maskToken = (token) => {
    if (!token) return '-';
    if (token.length <= 8) return '****';
    return token.substring(0, 4) + '****' + token.substring(token.length - 4);
  };

  const dealerColumns = [
    { title: '公司名稱', dataIndex: 'company_name', key: 'company_name', ellipsis: true },
    { title: '區域類型', dataIndex: 'region_type', key: 'region_type', width: 100 },
    { title: '代表人', dataIndex: 'representative', key: 'representative', width: 100 },
    { title: '聯絡人', dataIndex: 'contact_name', key: 'contact_name', width: 100 },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 200, ellipsis: true },
    { title: '電話', dataIndex: 'phone', key: 'phone', width: 130 },
    { title: 'Token', dataIndex: 'token', key: 'token', width: 140, render: (val) => <code>{maskToken(val)}</code> },
  ];

  const dealerRowSelection = {
    selectedRowKeys: selectedDealerKeys,
    onChange: (keys) => setSelectedDealerKeys(keys),
  };

  const renderDealerTab = () => (
    <div>
      <Card title="匯入經銷商" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <CampaignSelector />
          <Dragger
            accept=".xlsx,.xls"
            maxCount={1}
            fileList={dealerFile ? [dealerFile] : []}
            beforeUpload={(file) => {
              setDealerFile(file);
              return false;
            }}
            onRemove={() => setDealerFile(null)}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">點擊或拖曳 Excel 檔案至此處</p>
            <p className="ant-upload-hint">支援 .xlsx / .xls 格式</p>
          </Dragger>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleDealerImport}
            loading={dealerImporting}
            disabled={!selectedCampaign || !dealerFile}
          >
            匯入經銷商
          </Button>
        </Space>
      </Card>

      {dealerImportResult && (
        <Card style={{ marginBottom: 16 }}>
          <Result
            status={dealerImportResult.errors && dealerImportResult.errors.length > 0 ? 'warning' : 'success'}
            title="匯入完成"
            subTitle={`共 ${dealerImportResult.total || 0} 筆，成功 ${dealerImportResult.success || 0} 筆${dealerImportResult.errors && dealerImportResult.errors.length > 0 ? `，失敗 ${dealerImportResult.errors.length} 筆` : ''}`}
          >
            {dealerImportResult.errors && dealerImportResult.errors.length > 0 && (
              <div style={{ textAlign: 'left' }}>
                <p><strong>錯誤明細：</strong></p>
                <ul>
                  {dealerImportResult.errors.map((err, idx) => (
                    <li key={idx} style={{ color: '#ff4d4f' }}>{typeof err === 'string' ? err : err.message || JSON.stringify(err)}</li>
                  ))}
                </ul>
              </div>
            )}
          </Result>
        </Card>
      )}

      <Card
        title="經銷商列表"
        extra={
          <span style={{ color: '#888' }}>
            {selectedDealerKeys.length > 0 ? `已選 ${selectedDealerKeys.length} 筆` : ''}
          </span>
        }
      >
        <Table
          columns={dealerColumns}
          dataSource={dealers}
          rowKey="id"
          loading={dealersLoading}
          rowSelection={dealerRowSelection}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );

  // ============ Tab 3: Email Management ============

  useEffect(() => {
    if (selectedCampaign) {
      loadEmailLogs();
    }
  }, [selectedCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmailLogs = async () => {
    if (!selectedCampaign) return;
    setEmailLogsLoading(true);
    try {
      const res = await loiAPI.getEmailLogs(selectedCampaign);
      setEmailLogs(res.data || []);
    } catch (error) {
      message.error('載入發信記錄失敗');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const handleSendEmails = async () => {
    if (!selectedCampaign) {
      message.warning('請先選擇活動');
      return;
    }
    setSending(true);
    try {
      const payload = {
        dealer_ids: selectedDealerKeys.length > 0 ? selectedDealerKeys : undefined,
        email_type: 'initial',
      };
      const res = await loiAPI.sendEmails(selectedCampaign, payload);
      message.success(`發送完成，共寄出 ${res.data?.sent || 0} 封`);
      loadEmailLogs();
    } catch (error) {
      message.error(`發送失敗: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendReminders = async () => {
    if (!selectedCampaign) {
      message.warning('請先選擇活動');
      return;
    }
    setReminding(true);
    try {
      const res = await loiAPI.sendReminders(selectedCampaign);
      message.success(`催寄完成，共寄出 ${res.data?.sent || 0} 封`);
      loadEmailLogs();
    } catch (error) {
      message.error(`催寄失敗: ${error.message}`);
    } finally {
      setReminding(false);
    }
  };

  const emailTypeTag = (type) => {
    switch (type) {
      case 'initial':
        return <Tag color="blue">初次發送</Tag>;
      case 'reminder':
        return <Tag color="orange">催寄</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  const emailStatusTag = (status) => {
    switch (status) {
      case 'sent':
        return <Tag color="green">已發送</Tag>;
      case 'delivered':
        return <Tag color="green">已送達</Tag>;
      case 'failed':
        return <Tag color="red">失敗</Tag>;
      case 'pending':
        return <Tag color="gold">待發送</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const emailLogColumns = [
    { title: '經銷商', dataIndex: 'dealer_name', key: 'dealer_name', width: 160, ellipsis: true },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 200, ellipsis: true },
    { title: '類型', dataIndex: 'email_type', key: 'email_type', width: 100, render: emailTypeTag },
    { title: '發送時間', dataIndex: 'sent_at', key: 'sent_at', width: 180, render: (val) => val ? new Date(val).toLocaleString('zh-TW') : '-' },
    { title: '狀態', dataIndex: 'status', key: 'status', width: 100, render: emailStatusTag },
  ];

  const renderEmailTab = () => (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <CampaignSelector />
          <Space>
            <Popconfirm
              title="確認發送意向書"
              description={selectedDealerKeys.length > 0
                ? `將發送給已選取的 ${selectedDealerKeys.length} 位經銷商`
                : '將發送給所有經銷商'}
              onConfirm={handleSendEmails}
              okText="確認發送"
              cancelText="取消"
            >
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sending}
                disabled={!selectedCampaign}
              >
                發送意向書
                {selectedDealerKeys.length > 0 ? ` (${selectedDealerKeys.length})` : ' (全部)'}
              </Button>
            </Popconfirm>
            <Popconfirm
              title="確認一鍵催寄"
              description="將對尚未回覆的經銷商發送催寄信件"
              onConfirm={handleSendReminders}
              okText="確認催寄"
              cancelText="取消"
            >
              <Button
                icon={<MailOutlined />}
                loading={reminding}
                disabled={!selectedCampaign}
              >
                一鍵催寄
              </Button>
            </Popconfirm>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadEmailLogs}
              disabled={!selectedCampaign}
            >
              重新整理
            </Button>
          </Space>
        </Space>
      </Card>

      <Card title="發信記錄">
        <Table
          columns={emailLogColumns}
          dataSource={emailLogs}
          rowKey="id"
          loading={emailLogsLoading}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );

  // ============ Main Render ============

  const tabItems = [
    { key: 'products', label: '產品管理', children: renderProductTab() },
    { key: 'dealers', label: '經銷商管理', children: renderDealerTab() },
    { key: 'emails', label: '發信管理', children: renderEmailTab() },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2>意向書資料管理</h2>
      <Tabs items={tabItems} defaultActiveKey="products" />
    </div>
  );
}

export default LOIManagePage;
